import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Warning, ArrowRight, MicrosoftOutlookLogo } from "@phosphor-icons/react";

/**
 * Dashboard nudge shown when an Outlook connection has gone stale, so
 * unattended emails (weekly statements, reminders, invites) never silently
 * fall back to the mock outbox. Two cases:
 *   • the signed-in user's own mailbox needs reconnecting, or
 *   • (admins) any firm mailbox that sends automated email is expired.
 * Renders nothing when everything is healthy.
 */
export default function OutlookReconnectBanner({ className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selfStale, setSelfStale] = useState(false);
  const [firmExpired, setFirmExpired] = useState(0);

  useEffect(() => {
    let live = true;
    API.get("/outlook/status")
      .then((r) => { if (live) setSelfStale(!!r.data?.needs_reconnect); })
      .catch(() => {});
    if (user?.is_admin) {
      API.get("/admin/outlook/health")
        .then((r) => { if (live) setFirmExpired(r.data?.counts?.expired || 0); })
        .catch(() => {});
    }
    return () => { live = false; };
  }, [user?.is_admin]);

  if (!selfStale && !firmExpired) return null;

  const isSelf = selfStale;
  return (
    <div
      className={`rounded-lg border border-amber-300 bg-amber-50 p-4 ${className}`}
      data-testid="outlook-reconnect-banner"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-amber-100">
          <Warning weight="fill" className="h-5 w-5 text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-amber-900" data-testid="outlook-reconnect-title">
            {isSelf
              ? "Your Outlook connection has expired"
              : `${firmExpired} team mailbox${firmExpired === 1 ? "" : "es"} need reconnecting`}
          </h3>
          <p className="mt-1 text-xs text-amber-800">
            {isSelf
              ? "Emails you send and automated messages from your mailbox will silently fail until you reconnect."
              : "Automated emails (weekly statements, reminders, invites) from these mailboxes won't actually send until they're reconnected."}
          </p>
          <div className="mt-3">
            <Button
              size="sm"
              onClick={() => navigate(isSelf ? "/settings/integrations" : "/settings/integrations/health")}
              className="h-8 gap-1.5 bg-amber-600 text-xs text-white hover:bg-amber-700"
              data-testid="outlook-reconnect-btn"
            >
              <MicrosoftOutlookLogo weight="bold" className="h-3.5 w-3.5" />
              {isSelf ? "Reconnect Outlook" : "View connections"}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
